import { useEffect, useState } from 'react';
import { ClipboardList, X } from 'lucide-react';
import { ContactPicker } from '@/components/ContactPicker';
import type {
  Contact, DatenblattFelder, DatenblattOptions, DatenblattPatch, DatenblattResponse,
} from '@/types';

// Enum-Labels (nur UI; die Werte selbst müssen mit dem Feldkatalog der
// API — ExtractionController::DATENBLATT_FIELDS — übereinstimmen).
const unterlagenLabels: Record<string, string> = {
  offen: 'Offen',
  liegt_vor: 'Liegt vor',
  beauftragt: 'Beauftragt',
  wird_beauftragt: 'Wird beauftragt',
};

const kfwLabels: Record<string, string> = {
  offen: 'Offen',
  ean50: 'Ean50',
  andere: 'Andere',
};

const gebautVonLabels: Record<string, string> = {
  gps: 'GPS',
  kabel: 'Kabel',
};

const effizienzLabels: Record<string, string> = {
  geg: 'GEG',
  effizienzstandard_a_plus: 'Effizienzstandard A+',
  kfw40_nicht_foerderfaehig: 'KfW40 nicht förderfähig',
  kfw40_lca: 'KfW40 LCA',
  kfw40_qng: 'KfW40 QNG',
  eh55: 'EH55',
};

type FieldType =
  | 'text' | 'textarea' | 'date' | 'decimal' | 'bool'
  | 'unterlagen' | 'kfw' | 'effizienz' | 'gebaut' | 'infoblaetter' | 'quellen';

interface FieldSpec {
  key: keyof DatenblattFelder;
  label: string;
  type: FieldType;
}

/** Anzeige-/Eingabe-Reihenfolge = Aufbau des echten Datenblatt-Formulars. */
const FELDER: FieldSpec[] = [
  { key: 'gemarkung_flurstueck', label: 'Gemarkung / Flurstück', type: 'text' },
  { key: 'erstkontakt_am', label: 'Erstkontakt am', type: 'text' },
  { key: 'vertragssumme_brutto', label: 'Vertragssumme (brutto)', type: 'decimal' },
  { key: 'haustyp', label: 'Haustyp', type: 'text' },
  { key: 'vertrag_abgeschlossen_am', label: 'Vertrag abgeschlossen am', type: 'date' },
  { key: 'wohnflaeche_m2', label: 'Wohnfläche (m²)', type: 'decimal' },
  { key: 'gesamtflaeche_m2', label: 'Gesamtfläche (m²)', type: 'decimal' },
  { key: 'effizienzstandard', label: 'Effizienzstandard', type: 'effizienz' },
  { key: 'bodengutachten', label: 'Bodengutachten', type: 'unterlagen' },
  { key: 'bodengutachten_von', label: 'Bodengutachten von', type: 'text' },
  { key: 'hoehennivellement', label: 'Höhennivellement', type: 'unterlagen' },
  { key: 'hoehennivellement_von', label: 'Höhennivellement von', type: 'text' },
  { key: 'bauantrag_extern', label: 'Bauantrag extern', type: 'bool' },
  { key: 'bauantrag_extern_von', label: 'Bauantrag extern von', type: 'text' },
  { key: 'baugenehmigung_vorhanden', label: 'Baugenehmigung vorhanden', type: 'bool' },
  { key: 'kfw_baubegleitung', label: 'KfW-Baubegleitung', type: 'kfw' },
  { key: 'beauftragung_durch_bauherr', label: 'Begleitung durch Bauherrn beauftragt', type: 'bool' },
  { key: 'auftragsformular_unterzeichnet', label: 'Auftragsformular unterzeichnet', type: 'bool' },
  { key: 'zusatzkostenblatt', label: 'Zusatzkostenblatt', type: 'bool' },
  { key: 'schallschutz', label: 'Schallschutz', type: 'bool' },
  { key: 'angebot_garage', label: 'Angebot Garage', type: 'bool' },
  { key: 'angebot_garage_von', label: 'Garagenangebot von', type: 'text' },
  { key: 'sonstige_angebote', label: 'Sonstige Angebote', type: 'textarea' },
  { key: 'fremdmakler', label: 'Fremdmakler', type: 'bool' },
  { key: 'gebaut_von', label: 'Gebaut von', type: 'gebaut' },
  { key: 'infoblaetter', label: 'Infoblätter', type: 'infoblaetter' },
  { key: 'interessenten_quellen', label: 'Interessenten-Quellen', type: 'quellen' },
  { key: 'interessenten_hinweis', label: 'Hinweis zur Quelle', type: 'text' },
  { key: 'anmerkungen', label: 'Anmerkungen / Vereinbarungen', type: 'textarea' },
];

const inputClass =
  'w-full rounded border border-gray-200 px-2 py-1 text-sm text-gray-900 placeholder:text-gray-400 focus:border-primary-500 focus:outline-none disabled:bg-gray-50 disabled:text-gray-500';

/**
 * Geteilter Feld-Editor für alle Datenblatt-Katalogfelder — genutzt von
 * der Datenblatt-Karte (Formular) UND dem KI-Extraktions-Review (WP7),
 * damit beide dasselbe Formular-Layout und dieselben Labels haben.
 * onChange liefert Rohwerte: Strings ('' möglich), boolean, string[].
 */
export function DatenblattFelderEditor({
  fields, options, disabled, onChange,
}: {
  fields: DatenblattFelder;
  options: DatenblattOptions;
  disabled?: boolean;
  onChange: (key: keyof DatenblattFelder, value: string | boolean | string[]) => void;
}) {
  return (
    <div className="grid gap-x-6 gap-y-3 text-sm sm:grid-cols-2">
      {FELDER.map((spec) => {
        const raw = fields[spec.key];
        const rawText = raw as string | null;
        const rawList = raw as string[];
        const label = (
          <label className="mb-1 block text-xs font-medium text-gray-500">{spec.label}</label>
        );
        const wrap = (control: React.ReactNode, wide = false) => (
          <div key={spec.key} className={wide ? 'sm:col-span-2' : ''}>
            {label}
            {control}
          </div>
        );

        switch (spec.type) {
          case 'bool':
            return wrap(
              <label className="flex h-7 items-center gap-2 text-sm text-gray-900">
                <input
                  type="checkbox"
                  checked={!!raw}
                  disabled={disabled}
                  onChange={(e) => onChange(spec.key, e.target.checked)}
                  className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                />
                <span className="text-xs text-gray-600">{raw ? 'Ja' : 'Nein'}</span>
              </label>
            );
          case 'text':
          case 'decimal':
            return wrap(
              <input
                type="text"
                value={rawText ?? ''}
                disabled={disabled}
                placeholder={spec.type === 'decimal' ? 'z. B. 350.000,00' : ''}
                onChange={(e) => onChange(spec.key, e.target.value)}
                className={inputClass}
              />
            );
          case 'date':
            return wrap(
              <input
                type="date"
                value={rawText ?? ''}
                disabled={disabled}
                onChange={(e) => onChange(spec.key, e.target.value)}
                className={inputClass}
              />
            );
          case 'textarea':
            return wrap(
              <textarea
                rows={2}
                value={rawText ?? ''}
                disabled={disabled}
                onChange={(e) => onChange(spec.key, e.target.value)}
                className={inputClass}
              />,
              true
            );
          case 'unterlagen':
          case 'kfw':
          case 'gebaut': {
            const labelMap =
              spec.type === 'unterlagen' ? unterlagenLabels
              : spec.type === 'kfw' ? kfwLabels
              : gebautVonLabels;
            return wrap(
              <select
                value={rawText ?? ''}
                disabled={disabled}
                onChange={(e) => onChange(spec.key, e.target.value)}
                className={inputClass}
              >
                {spec.type === 'gebaut' && <option value="">—</option>}
                {Object.entries(labelMap).map(([value, text]) => (
                  <option key={value} value={value}>{text}</option>
                ))}
              </select>
            );
          }
          case 'effizienz':
            return wrap(
              <select
                value={rawText ?? ''}
                disabled={disabled}
                onChange={(e) => onChange(spec.key, e.target.value)}
                className={inputClass}
              >
                <option value="">—</option>
                {options.effizienzstandards.map((value) => (
                  <option key={value} value={value}>
                    {effizienzLabels[value] ?? value}
                  </option>
                ))}
              </select>
            );
          case 'infoblaetter':
          case 'quellen': {
            const vocab =
              spec.type === 'infoblaetter'
                ? options.infoblaetter
                : options.interessenten_quellen;
            const selected = rawList;
            const toggle = (key: string, checked: boolean) => {
              const next = checked
                ? [...selected, key]
                : selected.filter((k) => k !== key);
              onChange(spec.key, next);
            };
            return wrap(
              <div className="flex flex-wrap gap-x-4 gap-y-1.5 pt-1">
                {Object.entries(vocab).map(([key, text]) => (
                  <label key={key} className="inline-flex items-center gap-1.5 text-xs text-gray-700">
                    <input
                      type="checkbox"
                      checked={selected.includes(key)}
                      disabled={disabled}
                      onChange={(e) => toggle(key, e.target.checked)}
                      className="h-3.5 w-3.5 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                    />
                    {text}
                  </label>
                ))}
              </div>,
              true
            );
          }
        }
      })}
    </div>
  );
}

/**
 * Datenblatt-Karte (Phase 2): app-natives „_Datenblatt_“-Formular am
 * Bauvorhaben. Schreiben = Genehmiger+ oder Vertrieb am eigenen BV
 * (canEdit); ein Speichern-Button sendet nur die geänderten Felder
 * (Partial-Put). Planer/Kalkulator als Kontakt-Verweise (manuell —
 * die KI kennt keine IDs), Vertriebsmitarbeiter/Bauleiter read-only
 * aus den BV-Stammdaten.
 */
export function BauvorhabenDatenblattCard({
  data, canEdit, canPickContacts, vertrieblerName, bauleiterName, onSave,
}: {
  data: DatenblattResponse | null;
  canEdit: boolean;
  /** Kontakte-Modul aktiv (ContactPicker braucht die Kontakt-Suche). */
  canPickContacts: boolean;
  vertrieblerName: string | null;
  bauleiterName: string | null;
  onSave: (patch: DatenblattPatch) => Promise<void>;
}) {
  const server = data?.datenblatt;
  const [form, setForm] = useState<DatenblattFelder | null>(null);
  const [planerId, setPlanerId] = useState<number | null>(null);
  const [planerName, setPlanerName] = useState<string | null>(null);
  const [kalkulatorId, setKalkulatorId] = useState<number | null>(null);
  const [kalkulatorName, setKalkulatorName] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (!server) return;
    const fields: DatenblattFelder = {
      gemarkung_flurstueck: server.gemarkung_flurstueck,
      erstkontakt_am: server.erstkontakt_am,
      vertragssumme_brutto: server.vertragssumme_brutto,
      haustyp: server.haustyp,
      vertrag_abgeschlossen_am: server.vertrag_abgeschlossen_am,
      wohnflaeche_m2: server.wohnflaeche_m2,
      gesamtflaeche_m2: server.gesamtflaeche_m2,
      effizienzstandard: server.effizienzstandard,
      bodengutachten: server.bodengutachten,
      bodengutachten_von: server.bodengutachten_von,
      hoehennivellement: server.hoehennivellement,
      hoehennivellement_von: server.hoehennivellement_von,
      bauantrag_extern: server.bauantrag_extern,
      bauantrag_extern_von: server.bauantrag_extern_von,
      baugenehmigung_vorhanden: server.baugenehmigung_vorhanden,
      kfw_baubegleitung: server.kfw_baubegleitung,
      beauftragung_durch_bauherr: server.beauftragung_durch_bauherr,
      auftragsformular_unterzeichnet: server.auftragsformular_unterzeichnet,
      zusatzkostenblatt: server.zusatzkostenblatt,
      schallschutz: server.schallschutz,
      angebot_garage: server.angebot_garage,
      angebot_garage_von: server.angebot_garage_von,
      sonstige_angebote: server.sonstige_angebote,
      fremdmakler: server.fremdmakler,
      gebaut_von: server.gebaut_von,
      infoblaetter: server.infoblaetter,
      interessenten_quellen: server.interessenten_quellen,
      interessenten_hinweis: server.interessenten_hinweis,
      anmerkungen: server.anmerkungen,
    };
    setForm(fields);
    setPlanerId(server.planer_contact_id);
    setPlanerName(server.planer_name);
    setKalkulatorId(server.kalkulator_contact_id);
    setKalkulatorName(server.kalkulator_name);
  }, [server]);

  if (!data || !form || !server) {
    return (
      <div className="card p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
          <ClipboardList className="h-5 w-5 text-gray-400" />
          Datenblatt
        </h2>
        <p className="text-sm text-gray-500">Datenblatt konnte nicht geladen werden.</p>
      </div>
    );
  }

  const handleChange = (key: keyof DatenblattFelder, value: string | boolean | string[]) => {
    setForm((prev) => prev && ({ ...prev, [key]: value }) as DatenblattFelder);
  };

  // Patch = nur geänderte Felder; '' → null (leere Text-/Datums-/Zahlen-
  // Felder löschen, statt sie als '' zu senden).
  const buildPatch = (): DatenblattPatch => {
    // typisiertes Record → am Ende gesamt gecastet: die Korrelation
    // key↔Typ ist über FELDER garantiert, für TS aber nicht ausdrückbar.
    const patch: Record<string, unknown> = {};
    for (const spec of FELDER) {
      const value = form[spec.key];
      const before = server[spec.key];
      if (typeof value === 'boolean') {
        if (value !== before) patch[spec.key] = value;
      } else if (Array.isArray(value)) {
        const beforeList = (before as string[] | null) ?? [];
        if ([...value].sort().join(',') !== [...beforeList].sort().join(',')) {
          patch[spec.key] = value;
        }
      } else if ((value ?? null) !== (before ?? null)) {
        patch[spec.key] = value === '' ? null : value;
      }
    }
    if (planerId !== server.planer_contact_id) patch.planer_contact_id = planerId;
    if (kalkulatorId !== server.kalkulator_contact_id) patch.kalkulator_contact_id = kalkulatorId;
    return patch as DatenblattPatch;
  };

  const handleSave = async () => {
    const patch = buildPatch();
    if (Object.keys(patch).length === 0) return;
    setIsSaving(true);
    try {
      await onSave(patch);
    } finally {
      setIsSaving(false);
    }
  };

  const hasChanges = () => Object.keys(buildPatch()).length > 0;

  /** Beteiligten-Zeile: Kontakt-Verweis (Planer/Kalkulator) mit Picker. */
  const beteiligter = (
    label: string,
    contactName: string | null,
    setId: (id: number | null) => void,
    setName: (name: string | null) => void,
  ) => (
    <div className="flex min-w-0 items-center justify-between gap-2 py-2">
      <span className="text-xs font-medium text-gray-500">{label}</span>
      <div className="flex min-w-0 flex-1 items-center justify-end gap-2">
        {contactName ? (
          <span className="inline-flex items-center gap-1.5 rounded-full bg-gray-100 px-2.5 py-0.5 text-xs text-gray-800">
            {contactName}
            {canEdit && (
              <button
                type="button"
                onClick={() => { setId(null); setName(null); }}
                className="text-gray-400 hover:text-red-600"
                title="Verweis entfernen"
              >
                <X className="h-3 w-3" />
              </button>
            )}
          </span>
        ) : (
          <span className="text-xs text-gray-300">—</span>
        )}
        {canEdit && canPickContacts && (
          <ContactPicker
            placeholder={`${label} suchen…`}
            onSelect={(contact: Contact) => {
              setId(contact.id);
              setName(
                contact.display_name ||
                [contact.first_name, contact.last_name].filter(Boolean).join(' ') ||
                contact.company_name || 'Kontakt'
              );
            }}
          />
        )}
      </div>
    </div>
  );

  return (
    <div className="card p-6">
      <div className="flex flex-wrap items-center justify-between gap-2 mb-4">
        <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
          <ClipboardList className="h-5 w-5 text-gray-400" />
          Datenblatt
        </h2>
        {canEdit && hasChanges() && (
          <button
            type="button"
            onClick={handleSave}
            disabled={isSaving}
            className="btn btn-primary text-sm disabled:opacity-50"
          >
            {isSaving ? 'Speichern…' : 'Speichern'}
          </button>
        )}
      </div>

      {/* Beteiligte: Planer/Kalkulator als Kontakt-Verweise (manuell);
          Vertriebsmitarbeiter/Bauleiter kommen aus den BV-Stammdaten. */}
      <div className="mb-5 divide-y divide-gray-100 rounded-lg border border-gray-100 px-3">
        {beteiligter('Planer', planerName, setPlanerId, setPlanerName)}
        {beteiligter('Kalkulator', kalkulatorName, setKalkulatorId, setKalkulatorName)}
        <div className="flex items-center justify-between gap-2 py-2">
          <span className="text-xs font-medium text-gray-500">Vertriebsmitarbeiter</span>
          <span className="text-xs text-gray-800">{vertrieblerName || '—'}</span>
        </div>
        <div className="flex items-center justify-between gap-2 py-2">
          <span className="text-xs font-medium text-gray-500">Bauleiter</span>
          <span className="text-xs text-gray-800">{bauleiterName || '—'}</span>
        </div>
      </div>

      <DatenblattFelderEditor
        fields={form}
        options={data.options}
        disabled={!canEdit}
        onChange={handleChange}
      />

      {canEdit && hasChanges() && (
        <div className="mt-4 flex justify-end">
          <button
            type="button"
            onClick={handleSave}
            disabled={isSaving}
            className="btn btn-primary text-sm disabled:opacity-50"
          >
            {isSaving ? 'Speichern…' : 'Speichern'}
          </button>
        </div>
      )}
    </div>
  );
}